/**
 * Bastion design-system: standard chip / pill button style.
 *
 * Source of truth: strategy buttons on /radar (app/radar/page.tsx).
 * Shared across:
 *   - Heatmap period selector (1W / 1M / YTD / 1Y)
 *   - Score Movers strategy selector (GEAR+, Smart Value, …)
 *   - Universe Selector pills (General Watchlist, US 100, …)
 *
 * Use `bastionButtonStyle(...)` for the inline `style` prop, and
 * `bastionButtonHover(...)` inside onMouseEnter/Leave handlers to keep the
 * selected/non-selected backgrounds consistent.
 */

import type { CSSProperties, MouseEvent } from "react";
import { BASTION_ACCENT } from "@/lib/bastion-accent";

/** Body UI. Loaded in app/layout.tsx via next/font. Do not use raw "Inter". */
export const FONT_BASTION =
  'var(--font-inter), -apple-system, BlinkMacSystemFont, "Segoe UI", "Roboto", sans-serif';

export const FONT_HEADING =
  'var(--font-inter-tight), -apple-system, BlinkMacSystemFont, "Segoe UI", "Roboto", sans-serif';

export const FONT_MONO =
  'var(--font-jetbrains), ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, monospace';

/**
 * Neutral, hue-less accent for dropdown / popover option selection.
 *
 * Used as the "selected option" treatment across all settings-style dropdown
 * pickers (Key Stats, Footer, Universe, Strategy, Metric, Top-by-mcap, …) on
 * every page (Radar, Heatmap, Hub, Portfolios, …). Selection is expressed
 * through elevation — a lighter graphite fill + near-white label + a thin
 * metallic left bar — with ZERO chroma. Colour on this terminal is reserved
 * for meaning: Cool Porcelain (#DCE2EC, see `lib/bastion-accent.ts`) is the
 * single brand accent (primary CTAs, selected chrome), and green/yellow/red
 * belong to data cells / ratings only. Keeping picker chrome neutral lets
 * the data be the only thing that lights up.
 */
export const PICKER_ACCENT = "#c8ccd4";
export const PICKER_ACCENT_RGB = "200, 204, 212";
/** Near-white for the selected option label. */
export const PICKER_ACCENT_TEXT = "#f0f2f5";
export const PICKER_ACCENT_BG = "rgba(255, 255, 255, 0.08)";
export const PICKER_ACCENT_BG_HOVER = "rgba(255, 255, 255, 0.13)";
/** Neutral hover background for non-selected option rows. */
export const PICKER_ROW_HOVER_BG = "rgba(255, 255, 255, 0.05)";
/** Default resting color for a non-selected option label. */
export const PICKER_ROW_IDLE_COLOR = "rgba(255, 255, 255, 0.78)";

/**
 * Inline style for a dropdown option row's selection state (background, left
 * accent bar, label color). Spread into the row's `style`; pair with
 * `pickerOptionRowHoverBg` inside onMouseEnter / onMouseLeave.
 */
export function pickerOptionRowSelectionStyle(selected: boolean): CSSProperties {
  return {
    backgroundColor: selected ? PICKER_ACCENT_BG : "transparent",
    boxShadow: selected
      ? `inset 2px 0 0 ${PICKER_ACCENT}`
      : "inset 2px 0 0 transparent",
    color: selected ? PICKER_ACCENT_TEXT : PICKER_ROW_IDLE_COLOR,
  };
}

/** Background a dropdown option row should have for a given hover phase. */
export function pickerOptionRowHoverBg(
  selected: boolean,
  phase: "enter" | "leave",
): string {
  if (phase === "enter") {
    return selected ? PICKER_ACCENT_BG_HOVER : PICKER_ROW_HOVER_BG;
  }
  return selected ? PICKER_ACCENT_BG : "transparent";
}

/** Compact count badge on toolbar toggles (e.g. Filters). */
export function pickerActiveCountBadgeStyle(): CSSProperties {
  return {
    display: "inline-flex",
    alignItems: "center",
    justifyContent: "center",
    minWidth: 16,
    height: 16,
    padding: "0 4px",
    borderRadius: 999,
    background: `rgba(${PICKER_ACCENT_RGB}, 0.14)`,
    border: `1px solid rgba(${PICKER_ACCENT_RGB}, 0.38)`,
    color: PICKER_ACCENT,
    fontSize: 10,
    fontWeight: 700,
    fontFamily: FONT_BASTION,
    lineHeight: 1,
  };
}

/** Plain text label for the active table sort (e.g. "Sort Moment. ↓"). */
export function sortLabelStyle(): CSSProperties {
  return {
    fontSize: 13,
    fontFamily: FONT_BASTION,
    fontWeight: 500,
    whiteSpace: "nowrap",
    display: "inline-flex",
    alignItems: "center",
    gap: 4,
  };
}

/** Violet fill for the active sort-column header (Radar / tables). */
export const SORT_HEADER_ACCENT = "#782170";
export const SORT_HEADER_ACCENT_RGB = "120, 33, 112";
/** White label + sort caret on the violet header. */
export const SORT_HEADER_ACCENT_TEXT = "#ffffff";

/** Gradient fill + porcelain bottom accent for the sorted column header cell. */
export function sortedColumnHeaderCellStyle(): CSSProperties {
  return {
    background: `linear-gradient(180deg, rgba(${SORT_HEADER_ACCENT_RGB}, 0.55) 0%, rgba(${SORT_HEADER_ACCENT_RGB}, 0.22) 100%)`,
    color: SORT_HEADER_ACCENT_TEXT,
    fontWeight: 600,
    boxShadow: `inset 0 -2px 0 ${BASTION_ACCENT}`,
  };
}

/** "#facc15" → "250, 204, 21" (suitable for `rgba(${...}, 0.18)`). */
export function hexToRgb(hex: string): string {
  const h = hex.replace("#", "");
  const n = parseInt(h, 16);
  return `${(n >> 16) & 255}, ${(n >> 8) & 255}, ${n & 255}`;
}

/** Lighten a hex color towards white. amount 0 = original, 1 = white. */
export function lightenHex(hex: string, amount: number): string {
  const h = hex.replace("#", "");
  const n = parseInt(h, 16);
  const r = Math.round(((n >> 16) & 255) + (255 - ((n >> 16) & 255)) * amount);
  const g = Math.round(((n >> 8) & 255) + (255 - ((n >> 8) & 255)) * amount);
  const b = Math.round((n & 255) + (255 - (n & 255)) * amount);
  return `#${((1 << 24) | (r << 16) | (g << 8) | b).toString(16).slice(1)}`;
}

export interface BastionButtonOpts {
  /** Accent hex color (e.g. strategy color or "#facc15" for yellow). */
  color: string;
  /** Whether the button is currently selected/active. */
  isSelected: boolean;
  /** Optional override for height (default 28). */
  height?: number;
  /** Optional override for horizontal padding (default 12). */
  paddingX?: number;
}

/**
 * Returns the inline-style object for a Bastion-standard chip button.
 * Use together with `bastionButtonHover` inside mouse handlers.
 */
export function bastionButtonStyle({
  color,
  isSelected,
  height = 28,
  paddingX = 12,
}: BastionButtonOpts): CSSProperties {
  const cRgb = hexToRgb(color);
  const cLight = lightenHex(color, 0.35);
  return {
    // Active pops (brighter fill + defined border + soft ring); inactive stays
    // quiet with a dimmed label so the selected tab reads at a glance.
    background: isSelected
      ? `rgba(${cRgb}, 0.24)`
      : "rgba(255,255,255,0.04)",
    color: isSelected ? cLight : "rgba(255,255,255,0.6)",
    border: isSelected
      ? `1px solid rgba(${cRgb}, 0.6)`
      : "1px solid rgba(255,255,255,0.06)",
    borderRadius: "8px",
    padding: `2px ${paddingX}px`,
    fontSize: "12px",
    fontFamily: FONT_BASTION,
    fontWeight: isSelected ? 600 : 500,
    cursor: "pointer",
    // Chips are controls, not copyable text: stop macOS right-click from
    // selecting the label (e.g. when opening a chip link in a new tab).
    userSelect: "none",
    WebkitUserSelect: "none",
    transition: "all 0.15s ease",
    whiteSpace: "nowrap",
    height: `${height}px`,
    display: "flex",
    alignItems: "center",
    gap: "8px",
    backdropFilter: isSelected ? "none" : "blur(10px)",
    boxShadow: isSelected
      ? `inset 0 0 0 1px rgba(${cRgb}, 0.12), 0 0 0 3px rgba(${cRgb}, 0.06)`
      : "none",
    position: "relative",
    overflow: "hidden",
  };
}

/**
 * Returns the background color the button should have on hover (or on leave),
 * based on its selected state. Use inside onMouseEnter / onMouseLeave handlers:
 *
 *   onMouseEnter={(e) => { e.currentTarget.style.background = bastionButtonHover(color, isSelected, "enter"); }}
 *   onMouseLeave={(e) => { e.currentTarget.style.background = bastionButtonHover(color, isSelected, "leave"); }}
 */
export function bastionButtonHover(
  color: string,
  isSelected: boolean,
  phase: "enter" | "leave",
): string {
  const cRgb = hexToRgb(color);
  if (phase === "enter") {
    return isSelected ? `rgba(${cRgb}, 0.25)` : "rgba(255,255,255,0.08)";
  }
  return isSelected ? `rgba(${cRgb}, 0.18)` : "rgba(255,255,255,0.04)";
}

/** Color for an icon inside a Bastion button — accent when selected, faded otherwise. */
export function bastionButtonIconColor(color: string, isSelected: boolean): string {
  return isSelected ? lightenHex(color, 0.35) : "rgba(255,255,255,0.45)";
}

/** Light gray metallic CTA — auth gates, anonymous stock overlays, portfolio sign-in. */
export const BASTION_METALLIC_CTA_REST: CSSProperties = {
  background:
    "linear-gradient(145deg, rgba(255,255,255,0.055) 0%, rgba(200,202,210,0.035) 22%, rgba(255,255,255,0.02) 48%, rgba(140,142,150,0.04) 72%, rgba(255,255,255,0.03) 100%)",
  border: "1px solid rgba(255,255,255,0.12)",
  boxShadow:
    "inset 0 1px 0 rgba(255,255,255,0.11), inset 0 -1px 0 rgba(0,0,0,0.22), 0 2px 8px rgba(0,0,0,0.18)",
  color: "rgba(255,255,255,0.9)",
};

export const BASTION_METALLIC_CTA_HOVER: CSSProperties = {
  background:
    "linear-gradient(145deg, rgba(255,255,255,0.09) 0%, rgba(210,212,220,0.055) 22%, rgba(255,255,255,0.04) 48%, rgba(160,162,170,0.07) 72%, rgba(255,255,255,0.055) 100%)",
  borderColor: "rgba(255,255,255,0.22)",
  boxShadow:
    "inset 0 1px 0 rgba(255,255,255,0.14), inset 0 -1px 0 rgba(0,0,0,0.2), 0 4px 14px rgba(0,0,0,0.24)",
  color: "#fff",
};

export function bastionMetallicCtaStyle(overrides?: CSSProperties): CSSProperties {
  return {
    padding: "12px 28px",
    borderRadius: 10,
    fontSize: 14,
    fontWeight: 600,
    fontFamily: FONT_BASTION,
    letterSpacing: "-0.01em",
    cursor: "pointer",
    transition:
      "background 0.18s ease, border-color 0.18s ease, box-shadow 0.18s ease, color 0.18s ease",
    ...BASTION_METALLIC_CTA_REST,
    ...overrides,
  };
}

export function bastionMetallicCtaHoverHandlers() {
  return {
    onMouseEnter: (e: MouseEvent<HTMLElement>) => {
      Object.assign(e.currentTarget.style, {
        background: String(BASTION_METALLIC_CTA_HOVER.background),
        borderColor: String(BASTION_METALLIC_CTA_HOVER.borderColor),
        boxShadow: String(BASTION_METALLIC_CTA_HOVER.boxShadow),
        color: String(BASTION_METALLIC_CTA_HOVER.color),
      });
    },
    onMouseLeave: (e: MouseEvent<HTMLElement>) => {
      Object.assign(e.currentTarget.style, {
        background: String(BASTION_METALLIC_CTA_REST.background),
        borderColor: "rgba(255,255,255,0.12)",
        boxShadow: String(BASTION_METALLIC_CTA_REST.boxShadow),
        color: String(BASTION_METALLIC_CTA_REST.color),
      });
    },
  };
}
